import { api } from "@shared/api/client";
import type {
  ItComment,
  ItIncident,
  ItIndexResponse,
} from "../types/it";

export type CreateItIncidentPayload = {
  titulo: string;
  descripcion: string;
  observaciones?: string;
  prioridad: "baja" | "media" | "alta";
  categoria: string;
  proyecto_id?: number | null;
  attachments?: File[];
};

export type UpdateItObservationPayload = {
  tipo_observacion: "solicitante" | "responsable";
  observaciones?: string;
  observaciones_responsable?: string;
};

export async function getItData(): Promise<ItIndexResponse> {
  const { data } = await api.get<ItIndexResponse>("/it");
  return data;
}

export async function getItIncident(id: number): Promise<ItIncident> {
  const { data } = await api.get<{ data: ItIncident }>(`/it/${id}`);
  return data.data;
}

export async function createItIncident(
  payload: CreateItIncidentPayload,
): Promise<ItIncident> {
  const formData = new FormData();
  formData.append("titulo", payload.titulo);
  formData.append("descripcion", payload.descripcion);
  formData.append("prioridad", payload.prioridad);
  formData.append("categoria", payload.categoria);

  if (payload.observaciones?.trim()) {
    formData.append("observaciones", payload.observaciones.trim());
  }

  if (payload.proyecto_id) {
    formData.append("proyecto_id", String(payload.proyecto_id));
  }

  payload.attachments?.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data } = await api.post<{ ok: boolean; data: ItIncident }>("/it", formData);
  return data.data;
}

export async function updateItObservations(
  incidentId: number,
  payload: UpdateItObservationPayload,
): Promise<ItIncident> {
  const { data } = await api.patch<{ ok: boolean; data: ItIncident }>(
    `/it/${incidentId}/observations`,
    payload,
  );
  return data.data;
}

export async function reorderItIncidents(ids: number[]): Promise<void> {
  await api.post("/it/reorder", { ids });
}

export async function approveItIncident(id: number): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/approve`,
  );
  return data.data;
}

export async function rejectItRequest(id: number): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/reject-request`,
  );
  return data.data;
}

export async function assignItIncident(
  id: number,
  responsableId: number,
): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/assign`,
    { responsable_id: responsableId },
  );
  return data.data;
}

export async function startItIncident(id: number): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/start`,
  );
  return data.data;
}

export async function finishItIncident(
  id: number,
  tiempoDedicado: string,
): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/finish`,
    { tiempo_dedicado: tiempoDedicado },
  );
  return data.data;
}

export async function validateItIncident(
  id: number,
  accion: "aceptar" | "rechazar",
): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/validate`,
    { accion },
  );
  return data.data;
}

export async function reopenItIncident(id: number): Promise<ItIncident> {
  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${id}/reopen`,
  );
  return data.data;
}

export async function createItComment(
  incidentId: number,
  mensaje: string,
): Promise<ItComment> {
  const { data } = await api.post<{ ok: boolean; data: ItComment }>(
    `/it/${incidentId}/comments`,
    { mensaje },
  );
  return data.data;
}

export async function uploadItAttachments(
  incidentId: number,
  files: File[],
): Promise<ItIncident> {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data } = await api.post<{ ok: boolean; data: ItIncident }>(
    `/it/${incidentId}/attachments`,
    formData,
  );

  return data.data;
}
